import React, { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaTrophy,
  FaExclamationTriangle,
  FaBoxes,
  FaArrowUp,
  FaArrowDown,
  FaStore,
} from 'react-icons/fa'
import {
  ContentCard,
  FilterBar,
  StyledTable,
  TableWrapper,
  DistributorExpandRow,
  DistributorSubPanel,
  DistSkuTable,
} from './styles'
import { DistribuidorMix, DistributorSkuPerformance } from './types'

interface SkuDistributorMatrixProps {
  distribuidores: DistribuidorMix[]
}

const formatMoney = (val: number) => {
  return (val || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })
}

const formatNumber = (val: number) => {
  return Math.round(val || 0).toLocaleString('pt-BR')
}

export const SkuDistributorMatrix: React.FC<SkuDistributorMatrixProps> = ({ distribuidores }) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedDistId, setExpandedDistId] = useState<number | null>(null)
  const [distSubTab, setDistSubTab] = useState<Record<number, 'top' | 'bottom' | 'all'>>({})
  const [distSearch, setDistSearch] = useState<Record<number, string>>({})

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return distribuidores
    const t = searchTerm.toLowerCase().trim()
    return distribuidores.filter(
      (d) =>
        d.distribuidor.toLowerCase().includes(t) ||
        (d.regional && d.regional.toLowerCase().includes(t))
    )
  }, [distribuidores, searchTerm])

  const toggleExpand = (distId: number) => {
    setExpandedDistId((prev) => (prev === distId ? null : distId))
  }

  const getSubTab = (distId: number): 'top' | 'bottom' | 'all' => {
    return distSubTab[distId] || 'top'
  }

  const setSubTab = (distId: number, tab: 'top' | 'bottom' | 'all') => {
    setDistSubTab((prev) => ({ ...prev, [distId]: tab }))
  }

  const getDistSearch = (distId: number): string => {
    return distSearch[distId] || ''
  }

  const setDistSearchTerm = (distId: number, term: string) => {
    setDistSearch((prev) => ({ ...prev, [distId]: term }))
  }

  return (
    <ContentCard>
      <FilterBar>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: isLight ? '#0f172a' : '#fff' }}>
            Eficiência de Mix & Sortimento por Distribuidor
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 12, color: isLight ? '#64748b' : '#94a3b8' }}>
            Clique em um distribuidor para expandir e analisar os SKUs de maior saída (campeões) e os de baixo giro (críticos)
          </p>
        </div>

        <div className='search-wrapper'>
          <FaSearch className='search-icon' />
          <input
            type='text'
            placeholder='Buscar distribuidor ou regional...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </FilterBar>

      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: 44, textAlign: 'center' }}></th>
              <th>Distribuidor</th>
              <th>Regional</th>
              <th className='right'>PDVs Positivados</th>
              <th className='right'>SKUs Ativos</th>
              <th className='right'>Média SKUs / PDV</th>
              <th className='right'>Venda Média / PDV</th>
              <th className='right'>Volume Médio (Cx/SKU)</th>
              <th className='right'>Faturamento Total</th>
              <th className='right'>Volume Total (Cx)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => {
              const isExpanded = expandedDistId === d.distId
              const activeSubTab = getSubTab(d.distId)
              const searchInDist = getDistSearch(d.distId).toLowerCase().trim()

              // SKUs do distribuidor
              const allSkus = d.skus || []
              const topSkus = d.topSkus || allSkus.slice(0, 10)
              const bottomSkus = d.bottomSkus || [...allSkus].reverse().slice(0, 10)

              // Seleção de lista conforme a aba ativa
              let displayedSkus: DistributorSkuPerformance[] = []
              if (activeSubTab === 'top') {
                displayedSkus = topSkus
              } else if (activeSubTab === 'bottom') {
                displayedSkus = bottomSkus
              } else {
                displayedSkus = allSkus
              }

              // Filtro de busca interna
              if (searchInDist) {
                displayedSkus = displayedSkus.filter(
                  (s) =>
                    s.sku.toLowerCase().includes(searchInDist) ||
                    s.descricao.toLowerCase().includes(searchInDist) ||
                    s.categoria.toLowerCase().includes(searchInDist)
                )
              }

              // Pareto Top 5 Concentration
              const top5Fat = topSkus.slice(0, 5).reduce((acc, s) => acc + s.faturamento, 0)
              const paretoTop5Pct = d.faturamento > 0 ? (top5Fat / d.faturamento) * 100 : 0

              return (
                <React.Fragment key={d.distId}>
                  <tr
                    onClick={() => toggleExpand(d.distId)}
                    style={{
                      cursor: 'pointer',
                      background: isExpanded ? (isLight ? 'rgba(166, 193, 11, 0.15)' : 'rgba(166, 193, 11, 0.08)') : undefined,
                      transition: 'background 0.2s',
                    }}
                    title='Clique para expandir ou recolher os SKUs deste distribuidor'
                  >
                    <td style={{ textAlign: 'center', color: isExpanded ? '#a6c10b' : '#64748b' }}>
                      {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </td>
                    <td style={{ fontWeight: 600, color: isLight ? '#0f172a' : '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{d.distribuidor}</span>
                        {isExpanded && (
                          <span
                            style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#a6c10b',
                              color: '#001842',
                              fontWeight: 700,
                            }}
                          >
                            EXPANDIDO
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ color: isLight ? '#64748b' : '#94a3b8' }}>{d.regional || '-'}</td>
                    <td className='right'>{formatNumber(d.totalPdvs)}</td>
                    <td className='right'>{d.skusAtivos}</td>
                    <td className='right' style={{ color: isLight ? '#15803d' : '#a6c10b', fontWeight: 700 }}>
                      {d.mediaSkusPorPdv?.toFixed(1)} SKUs
                    </td>
                    <td className='right'>{formatMoney(d.vendaMediaPorSku)}</td>
                    <td className='right'>{formatNumber(d.volumeMedioPorSku)} cx</td>
                    <td className='right' style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#fff' }}>
                      {formatMoney(d.faturamento)}
                    </td>
                    <td className='right'>{formatNumber(d.caixas)} cx</td>
                  </tr>

                  {/* Subpainel Expandido de SKUs */}
                  {isExpanded && (
                    <DistributorExpandRow>
                      <td colSpan={10}>
                        <DistributorSubPanel>
                          <div className='subpanel-header'>
                            <div className='dist-title'>
                              <h4>Diagnóstico de Mix de SKUs: {d.distribuidor}</h4>
                              <span className='badge'>{d.skusAtivos} SKUs Faturados</span>
                              <span className='badge' style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', borderColor: 'rgba(56, 189, 248, 0.3)' }}>
                                {formatNumber(d.totalPdvs)} PDVs Ativos
                              </span>
                            </div>

                            <div className='subpanel-controls'>
                              <div className='subtab-group'>
                                <button
                                  type='button'
                                  className={activeSubTab === 'top' ? 'active top' : ''}
                                  onClick={() => setSubTab(d.distId, 'top')}
                                >
                                  <FaArrowUp size={11} />
                                  <FaTrophy size={11} />
                                  Mais Vendidos ({topSkus.length})
                                </button>
                                <button
                                  type='button'
                                  className={activeSubTab === 'bottom' ? 'active bottom' : ''}
                                  onClick={() => setSubTab(d.distId, 'bottom')}
                                >
                                  <FaArrowDown size={11} />
                                  <FaExclamationTriangle size={11} />
                                  Baixo Giro / Críticos ({bottomSkus.length})
                                </button>
                                <button
                                  type='button'
                                  className={activeSubTab === 'all' ? 'active all' : ''}
                                  onClick={() => setSubTab(d.distId, 'all')}
                                >
                                  <FaBoxes size={11} />
                                  Todos os SKUs ({allSkus.length})
                                </button>
                              </div>

                              <input
                                type='text'
                                className='mini-search'
                                placeholder='Buscar SKU ou descrição...'
                                value={getDistSearch(d.distId)}
                                onChange={(e) => setDistSearchTerm(d.distId, e.target.value)}
                              />
                            </div>
                          </div>

                          <div className='subpanel-kpis'>
                            <div className='sub-kpi-card'>
                              <span className='kpi-title'>Concentração Pareto (Top 5)</span>
                              <span className='kpi-val' style={{ color: paretoTop5Pct > 60 ? '#facc15' : '#4ade80' }}>
                                {paretoTop5Pct.toFixed(1)}%
                              </span>
                            </div>
                            <div className='sub-kpi-card'>
                              <span className='kpi-title'>Faturamento Médio por SKU</span>
                              <span className='kpi-val'>{formatMoney(d.vendaMediaPorSku)}</span>
                            </div>
                            <div className='sub-kpi-card'>
                              <span className='kpi-title'>Profundidade de Sortimento</span>
                              <span className='kpi-val' style={{ color: '#a6c10b' }}>
                                {d.mediaSkusPorPdv?.toFixed(1)} SKUs/PDV
                              </span>
                            </div>
                            <div className='sub-kpi-card'>
                              <span className='kpi-title'>Itens em Visualização</span>
                              <span className='kpi-val'>{displayedSkus.length} de {allSkus.length}</span>
                            </div>
                          </div>

                          <DistSkuTable>
                            <thead>
                              <tr>
                                <th style={{ width: 45, textAlign: 'center' }}>Rank</th>
                                <th>Código SKU & Descrição</th>
                                <th>Categoria & Família</th>
                                <th className='center' style={{ width: 140 }}>PDVs Positivados</th>
                                <th className='right' style={{ width: 130 }}>Volume (Cx)</th>
                                <th className='right' style={{ width: 140 }}>Faturamento</th>
                                <th className='center' style={{ width: 130 }}>Share no Mix</th>
                                <th className='center' style={{ width: 140 }}>Diagnóstico</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayedSkus.length === 0 ? (
                                <tr>
                                  <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                                    Nenhum SKU encontrado com o filtro aplicado.
                                  </td>
                                </tr>
                              ) : (
                                displayedSkus.map((s, idx) => {
                                  // Determina classe do badge de ranking
                                  let rankClass = 'default'
                                  if (activeSubTab === 'bottom') {
                                    rankClass = 'warning'
                                  } else if (s.rank === 1) {
                                    rankClass = 'gold'
                                  } else if (s.rank === 2) {
                                    rankClass = 'silver'
                                  } else if (s.rank === 3) {
                                    rankClass = 'bronze'
                                  }

                                  // Status do SKU
                                  const isTop = s.rank <= 5
                                  const isBottom = activeSubTab === 'bottom' || s.rank > Math.max(allSkus.length - 5, 1)

                                  return (
                                    <tr key={s.sku + idx}>
                                      <td className='center'>
                                        <span className={`rank-badge ${rankClass}`}>
                                          #{s.rank || idx + 1}
                                        </span>
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#ffffff' }}>
                                              {s.sku}
                                            </span>
                                            <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#94a3b8', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)', padding: '1px 6px', borderRadius: '4px' }}>
                                              {s.marca || 'GSA'}
                                            </span>
                                          </div>
                                          <span style={{ fontSize: '12px', color: isLight ? '#475569' : '#cbd5e1' }}>
                                            {s.descricao}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                          <span style={{ color: isLight ? '#334155' : '#e2e8f0', fontWeight: 600 }}>{s.categoria}</span>
                                          <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#94a3b8' }}>{s.familia}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className='bar-container'>
                                          <div className='bar-bg'>
                                            <div
                                              className={`bar-fill ${s.penetracaoPct > 30 ? 'green' : s.penetracaoPct > 10 ? 'blue' : 'red'}`}
                                              style={{ width: `${Math.min(s.penetracaoPct, 100)}%` }}
                                            />
                                          </div>
                                          <span className='bar-label'>
                                            {formatNumber(s.pdvsPositivados)} ({s.penetracaoPct}%)
                                          </span>
                                        </div>
                                      </td>
                                      <td className='right'>{formatNumber(s.caixas)} cx</td>
                                      <td className='right' style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#ffffff' }}>
                                        {formatMoney(s.faturamento)}
                                      </td>
                                      <td>
                                        <div className='bar-container'>
                                          <div className='bar-bg'>
                                            <div
                                              className={`bar-fill ${activeSubTab === 'bottom' ? 'red' : 'green'}`}
                                              style={{ width: `${Math.min(s.shareFaturamento * 3, 100)}%` }}
                                            />
                                          </div>
                                          <span className='bar-label'>{s.shareFaturamento}%</span>
                                        </div>
                                      </td>
                                      <td className='center'>
                                        {isTop && activeSubTab !== 'bottom' ? (
                                          <span className='status-badge success'>Alta Saída</span>
                                        ) : isBottom ? (
                                          <span className='status-badge warning'>Baixo Giro</span>
                                        ) : (
                                          <span className='status-badge neutral'>Giro Regular</span>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })
                              )}
                            </tbody>
                          </DistSkuTable>
                        </DistributorSubPanel>
                      </td>
                    </DistributorExpandRow>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </ContentCard>
  )
}

